import { call, put, select, takeEvery } from "redux-saga/effects";
import { PayloadAction } from "@reduxjs/toolkit";
import { format, subDays } from "date-fns";

import { supabase } from "@/lib/supabase";
import {
  OVERVIEW_DOMAINS_AGG_VIEW_NAME,
  OVERVIEW_SOURCE_DETAIL_LINKS_VIEW_NAME,
} from "../const";
import { selectAuthAccountId } from "../selectors/auth";
import {
  selectSelectedBrandIdState,
  selectViewBrandIdState,
} from "../selectors/brands";
import { applySagaError } from "../utils/saga";
import { throwOnSupabaseError } from "../../utils/errors";
import {
  fetchOverviewDomains,
  fetchOverviewDomainsSuccess,
  fetchOverviewDomainsFailure,
  fetchSourceDomainsChartData,
  fetchSourceDomainsChartDataSuccess,
  fetchSourceDomainsChartDataFailure,
  fetchSourceDetailLinks,
  fetchSourceDetailLinksSuccess,
  fetchSourceDetailLinksFailure,
} from "./slice";
import type {
  FetchOverviewDomainsPayload,
  FetchSourceDomainsChartPayload,
  OverviewDomainsAggRow,
  FetchSourceDetailLinksPayload,
  SourceDetailLinkRow,
} from "./types";

/**
 * UI context uses viewBrandId (see selectSelectedBrandState). Persisted users_meta id must not override it.
 */
function resolveOverviewBrandId(
  viewBrandId: number | null,
  selectedBrandId: number | null | undefined
): number | null {
  if (viewBrandId != null) return viewBrandId;
  if (selectedBrandId != null) return selectedBrandId;
  return null;
}

export function* fetchOverviewDomainsSaga(
  action: PayloadAction<FetchOverviewDomainsPayload>
) {
  try {
    const userID: string | null = yield select(selectAuthAccountId);
    const selectedBrandId: number | null | undefined = yield select(
      selectSelectedBrandIdState
    );
    const viewBrandId: number | null = yield select(selectViewBrandIdState);
    const brandId = resolveOverviewBrandId(viewBrandId, selectedBrandId);

    if (userID == null || brandId == null) {
      yield put(fetchOverviewDomainsSuccess([]));
      return;
    }

    const payload = action.payload;
    const modelId = "model_id" in payload ? payload.model_id : undefined;

    let query = supabase
      .from(OVERVIEW_DOMAINS_AGG_VIEW_NAME)
      .select("*")
      .eq("user_id", userID)
      .eq("brand_id", brandId)
      .order("links_count", { ascending: false });

    if ("date" in payload) {
      query = query.eq("response_date", payload.date);
    } else {
      const endDate = new Date();
      const startDate = subDays(endDate, payload.period);
      const startStr = format(startDate, "yyyy-MM-dd");
      const endStr = format(endDate, "yyyy-MM-dd");
      query = query.gte("response_date", startStr).lte("response_date", endStr);
    }

    if (modelId != null) {
      query = query.eq("model_id", modelId);
    }

    const { data, error } = yield call(() => query);

    throwOnSupabaseError(error);

    const rows = (data ?? []) as OverviewDomainsAggRow[];
    yield put(fetchOverviewDomainsSuccess(rows));
  } catch (error) {
    yield* applySagaError(error, {
      operationName: "overview.fetchOverviewDomains",
      failureAction: fetchOverviewDomainsFailure,
    });
  }
}

const SOURCE_CHART_PAGE_SIZE = 5000;

function* fetchSourceDomainsChartDataSaga(
  action: PayloadAction<FetchSourceDomainsChartPayload>
) {
  try {
    const userID: string | null = yield select(selectAuthAccountId);
    const selectedBrandId: number | null | undefined = yield select(
      selectSelectedBrandIdState
    );
    const viewBrandId: number | null = yield select(selectViewBrandIdState);
    const brandId = resolveOverviewBrandId(viewBrandId, selectedBrandId);

    if (userID == null || brandId == null) {
      yield put(fetchSourceDomainsChartDataSuccess([]));
      return;
    }

    const payload = action.payload;
    const modelId = "model_id" in payload ? payload.model_id : undefined;
    const search = payload.search?.trim();

    let query = supabase
      .from(OVERVIEW_DOMAINS_AGG_VIEW_NAME)
      .select("*")
      .eq("user_id", userID)
      .eq("brand_id", brandId)
      .order("links_count", { ascending: false })
      .range(0, SOURCE_CHART_PAGE_SIZE - 1);

    if ("date" in payload) {
      query = query.eq("response_date", payload.date);
    } else {
      const endDate = new Date();
      const startDate = subDays(endDate, payload.period);
      const startStr = format(startDate, "yyyy-MM-dd");
      const endStr = format(endDate, "yyyy-MM-dd");
      query = query.gte("response_date", startStr).lte("response_date", endStr);
    }

    if (modelId != null) {
      query = query.eq("model_id", modelId);
    }

    if (search && search.length > 0) {
      query = query.ilike("domain", `%${search}%`);
    }

    const { data, error } = yield call(() => query);

    throwOnSupabaseError(error);

    const rows = (data ?? []) as OverviewDomainsAggRow[];
    yield put(fetchSourceDomainsChartDataSuccess(rows));
  } catch (error) {
    yield* applySagaError(error, {
      operationName: "overview.fetchSourceDomainsChartData",
      failureAction: fetchSourceDomainsChartDataFailure,
    });
  }
}

function* fetchSourceDetailLinksSaga(
  action: PayloadAction<FetchSourceDetailLinksPayload>
) {
  try {
    const userID: string | null = yield select(selectAuthAccountId);
    const selectedBrandId: number | null | undefined = yield select(
      selectSelectedBrandIdState
    );
    const viewBrandId: number | null = yield select(selectViewBrandIdState);
    const brandId = resolveOverviewBrandId(viewBrandId, selectedBrandId);

    if (userID == null || brandId == null) {
      yield put(fetchSourceDetailLinksSuccess([]));
      return;
    }

    const payload = action.payload;
    const { domain_id } = payload;
    const modelId = "model_id" in payload ? payload.model_id : undefined;

    let linksQuery = supabase
      .from(OVERVIEW_SOURCE_DETAIL_LINKS_VIEW_NAME)
      .select(
        "id, url, is_citation, response_id, is_mentioned, response_date"
      )
      .eq("user_id", userID)
      .eq("brand_id", brandId)
      .eq("domain_id", domain_id)
      .order("id", { ascending: true });

    if ("date" in payload) {
      linksQuery = linksQuery.eq("response_date", payload.date);
    } else {
      const endDate = new Date();
      const startDate = subDays(endDate, payload.period);
      const startStr = format(startDate, "yyyy-MM-dd");
      const endStr = format(endDate, "yyyy-MM-dd");
      linksQuery = linksQuery
        .gte("response_date", startStr)
        .lte("response_date", endStr);
    }

    if (modelId != null) {
      linksQuery = linksQuery.eq("model_id", modelId);
    }

    const { data: linksData, error: linksError } = yield call(() =>
      linksQuery
    );

    throwOnSupabaseError(linksError);

    const viewRows = (linksData ?? []) as {
      id: number;
      url: string;
      is_citation: boolean;
      response_id: number;
      is_mentioned: boolean;
      response_date: string;
    }[];

    const mapped: SourceDetailLinkRow[] = viewRows.map((r, i) => ({
      id: r.id,
      index: i + 1,
      url: r.url,
      title: "",
      is_mentioned: r.is_mentioned,
      user: 1,
      citations: r.is_citation ? 1 : 0,
      response_id: r.response_id,
      response_date: r.response_date ?? "",
    }));

    yield put(fetchSourceDetailLinksSuccess(mapped));
  } catch (error) {
    yield* applySagaError(error, {
      operationName: "overview.fetchSourceDetailLinks",
      failureAction: fetchSourceDetailLinksFailure,
    });
  }
}

export function* overviewSaga() {
  yield takeEvery(fetchOverviewDomains.type, fetchOverviewDomainsSaga);
  yield takeEvery(fetchSourceDomainsChartData.type, fetchSourceDomainsChartDataSaga);
  yield takeEvery(fetchSourceDetailLinks.type, fetchSourceDetailLinksSaga);
}
